import type { Maybe } from '@adornis/base/utilTypes.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { FormOfFinancing } from '../../_cms/financing/entities/FormOfFinancing.js';
import { DurchfuehrungsTag } from '../../_cms/products/entities/Product.js';
import { Adresse } from '../../adresse.js';
import { FormOfFinancingLogicExpression } from '../../master-data-management/kpf/entities/FormOfFinancingLogicExpression.js';
import { KPF } from '../../master-data-management/kpf/entities/KPF.js';
import { School } from '../../master-data-management/partner/entities/School.js';
import { ChancenwerkUser } from '../../master-data-management/users/entities/ChancenwerkUser.js';
import type { Mitarbeiter } from '../../master-data-management/users/entities/Mitarbeiter.js';
import { ChancenRole } from '../../semantics/ChancenRole.js';
import { Role } from '../../semantics/Role.js';
import { SemanticRelationship } from '../../semantics/SemanticRelationship.js';
import type { Taetigkeit } from '../../semantics/Taetigkeit.js';

const resolveGetPublicKPFInformationByID = <T = Array<EntityData<KPF>>>(ids?: string[]) => {
  return async (gqlFields: BaseQLSelectionSet<KPF>) => {
    const formOfFionanzingSelector: BaseQLSelectionSet<FormOfFinancingLogicExpression | FormOfFinancing> = {
      ...FormOfFinancingLogicExpression.allFields,
      ...FormOfFinancing.allFields,
    };

    const kpfCollection = await getRawCollection<EntityData<KPF>>(KPF._collectionName);
    return (await kpfCollection
      .aggregate([
        ...(ids?.length
          ? [
              {
                $match: { _id: { $in: ids } },
              },
            ]
          : []),
        { $lookup: { from: School._collectionName, as: 'schools', localField: 'schoolID', foreignField: '_id' } },
        { $addFields: { school: { $arrayElemAt: ['$schools', 0] } } },
        {
          $project: {
            _class: 1,
            _id: 1,
            startDatum: 1,
            endDatum: 1,
            email: 1,
            produkt: {
              _id: 1,
              _class: 1,
              kuerzel: 1,
              durchfuehrungsTage: DurchfuehrungsTag.allFields,
            },
            schoolID: 1,
            school: {
              _id: 1,
              _class: 1,
              name: 1,
              address: Adresse.allFields,
              kuerzel: 1,
            },
            standardMembershipFee: 1,
            formOfFinanzingLogicString: formOfFionanzingSelector,
          },
        },
      ])
      .toArray()) as T;
  };
};

const getPublicKPFInformationByID = registerQuery({
  type: () => [KPF],
  operationName: 'getPublicKPFInformationByID',
  params: [{ type: () => [String], name: 'schoolIDs' }],
  resolve: resolveGetPublicKPFInformationByID<KPF[]>,
});

const getAllPublicKPFInformation = registerQuery({
  type: () => [KPF],
  operationName: 'getAllPublicKPFInformation',
  resolve: resolveGetPublicKPFInformationByID<KPF[]>,
});

const resolveGetPublicKPFInformationBySchool = (schoolIDs?: string[]) => {
  return async (gqlFields: BaseQLSelectionSet<KPF>) => {
    const formOfFinanzingSelector: BaseQLSelectionSet<FormOfFinancingLogicExpression | FormOfFinancing> = {
      ...FormOfFinancingLogicExpression.allFields,
      ...FormOfFinancing.allFields,
    };

    const kpfCollection = await getRawCollection<EntityData<KPF>>(KPF._collectionName);
    return (await kpfCollection
      .aggregate([
        ...(schoolIDs?.length
          ? [
              {
                $match: { schoolID: { $in: schoolIDs } },
              },
            ]
          : []),
        { $lookup: { from: School._collectionName, as: 'schools', localField: 'schoolID', foreignField: '_id' } },
        { $addFields: { school: { $arrayElemAt: ['$schools', 0] } } },
        {
          $project: {
            _class: 1,
            _id: 1,
            startDatum: 1,
            endDatum: 1,
            email: 1,
            produkt: {
              _class: 1,
              kuerzel: 1,
              durchfuehrungsTage: DurchfuehrungsTag.allFields,
            },
            schoolID: 1,
            school: {
              _id: 1,
              _class: 1,
              name: 1,
              address: Adresse.allFields,
            },
            schukoTel: 1,
            standardMembershipFee: 1,
            formOfFinanzingLogicString: formOfFinanzingSelector,
          },
        },
      ])
      .toArray()) as Array<EntityData<KPF>>;
  };
};

const getPublicKPFInformationBySchool = registerQuery({
  type: () => [KPF],
  operationName: 'getPublicKPFInformationBySchool',
  params: [{ type: () => [String], name: 'schoolIDs' }],
  resolve: resolveGetPublicKPFInformationBySchool,
});

const resolveGetPublicKPFOperativePerson = (kpfID: string) => {
  return async () => {
    const TaetigkeitClass = (await import('../../semantics/Taetigkeit.js')).Taetigkeit;
    const semanticCol = await getRawCollection<EntityData<Taetigkeit>>(SemanticRelationship._collectionName);
    const userCol = await getRawCollection<EntityData<ChancenwerkUser>>(ChancenwerkUser._collectionName);

    const predicate = (await loadMongoData(ChancenRole, { name: Role.INTERNAL_KPF_OPERATIV }))[0];
    const relationshipsForKPF = await semanticCol
      .find(
        {
          objectID: kpfID,
          predicateID: predicate?._id ?? '',
          ...TaetigkeitClass.isActiveMongoSelector(),
        },
        {
          projection: {
            _id: 1,
            subjectID: 1,
          },
        },
      )
      .toArray();
    const ops = (await userCol
      .find(
        { _id: { $in: relationshipsForKPF.map(rel => rel.subjectID ?? '') } },
        {
          projection: {
            _id: 1,
            title: 1,
            vorname: 1,
            nachname: 1,
            email: 1,
          },
        },
      )
      .toArray()) as Maybe<Mitarbeiter[]>;

    if (ops && ops.length > 0) {
      return [[ops[0]?.title, ops[0]?.vorname, ops[0]?.nachname].filter(Boolean).join(' '), ops[0]?.email ?? ''];
    }
    return ['Unbekannt', ''];
  };
};

const getPublicKPFOperativePerson = registerQuery({
  type: () => [String],
  operationName: 'getPublicKPFOperativePerson',
  params: [{ type: () => String, name: 'kpfID' }],
  resolve: resolveGetPublicKPFOperativePerson,
});

export namespace KPFPublicInformationAPI {
  export const getAll = getAllPublicKPFInformation;
  export const getByID = getPublicKPFInformationByID;
  export const getBySchoolID = getPublicKPFInformationBySchool;
  export const getOperative = getPublicKPFOperativePerson;
}
